// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import FancyScroll from "@eisinc/App/Components/Common/FancyScroll";
import updateCartLinesCertifications from "@eisinc/App/Handlers/Pages/Cart/UpdateCartLinesCertifications";
import { CustomAppState } from "@eisinc/App/Store/Reducers";
import Button, { ButtonPresentationProps } from "@eisinc/MobiusOverrides/Button";
import Checkbox from "@eisinc/MobiusOverrides/Checkbox/Checkbox";
import DataTable from "@eisinc/MobiusOverrides/DataTable/DataTable";
import DataTableBody from "@eisinc/MobiusOverrides/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@eisinc/MobiusOverrides/DataTable/DataTableCell";
import DataTableHead from "@eisinc/MobiusOverrides/DataTable/DataTableHead";
import DataTableHeader from "@eisinc/MobiusOverrides/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@eisinc/MobiusOverrides/DataTable/DataTableRow";
import LazyImage, { LazyImageProps } from "@eisinc/MobiusOverrides/LazyImage";
import ProductBrand, { ProductBrandStyles } from "@eisinc/Overrides/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@eisinc/Overrides/Components/ProductDescription";
import ProductPartNumbers from "@eisinc/Overrides/Components/ProductPartNumbers";
import { fontConfig } from "@eisinc/XDL/PreStyle/typography";
import {
    color,
    modalButtonsActionStyles,
    sharedProductDescriptionStyles,
    styledScrollbar,
    xScreen,
} from "@eisinc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { CartModel } from "@insite/client-framework/Types/ApiModels";
import { DataTableHeaderPresentationProps } from "@insite/mobius/DataTable/DataTableHeader";
import { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    return {
        isMobileScreen: (state as CustomAppState).xCommon.isMobileScreen,
    };
};

const mapDispatchToProps = {
    updateCartLinesCertifications,
};
interface OwnProps {
    isOpen?: boolean;
    closeModalHandler: () => void;
    cart: CartModel;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

interface RequestCerificationModalStyles {
    modal?: ModalPresentationProps;
    wrapper?: InjectableCss;
    addAllToListLink?: LinkPresentationProps;
    removeAllLink?: LinkPresentationProps;
    requestCertificationsWrapper?: InjectableCss;
    requestCertificationsLink?: LinkPresentationProps;
    leftSection?: InjectableCss;
    rightSection?: InjectableCss;
    removeModalButtonsWrapper?: InjectableCss;
    cancelButton?: ButtonPresentationProps;
    removeButton?: ButtonPresentationProps;
    requestCertificationsModal?: ModalPresentationProps;
    requestCertificationsModalWrapper?: InjectableCss;
    requestCertificationsModalSelectAllChecboxesWrapper?: InjectableCss;
    requestCertificationsModalSubcopy?: TypographyPresentationProps;
    requestCertificationsModalProductsSelectionTableWrapper?: InjectableCss;
    productDataTableCell?: DataTableHeaderPresentationProps;
    productImageAndInfoWrapper?: InjectableCss;
    productImage?: LazyImageProps;
    productInfoWrapper?: InjectableCss;
    productDescriptionStyles?: ProductDescriptionStyles;
    productBrandStyles?: ProductBrandStyles;
    checkboxDataTableCell?: DataTableCellProps;
    requestCertificationsModalButtonsWrapper?: InjectableCss;
    requestCertificationsModalCancelButton?: ButtonPresentationProps;
    requestCertificationsModalUpdateRequestsButton?: ButtonPresentationProps;
    dataTableHeader?: DataTableHeaderPresentationProps;
    dataTableRow?: DataTableRowProps;
}

export const requestCerificationModalStyles: RequestCerificationModalStyles = {
    requestCertificationsModalWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 16px;
        `,
    },

    requestCertificationsModalSelectAllChecboxesWrapper: {
        css: css`
            border: 0;
            padding: 0;
            margin: 0;
            display: flex;
            padding-top: 16px;
            flex-wrap: wrap;
            align-items: flex-start;
            border-top: 1px solid ${color("common.border")};
            > div {
                flex: 1;
            }
            ${xScreen("lt", "sm")} {
                flex-direction: column;
                row-gap: 16px;
            }
        `,
    },

    requestCertificationsModalSubcopy: {
        ...fontConfig.small,
    },

    requestCertificationsModalProductsSelectionTableWrapper: {
        css: css`
            max-height: calc(100vh - 400px);
            padding-right: 12px;
            ${styledScrollbar()}
        `,
    },

    productDataTableCell: {
        css: css`
            white-space: unset;
            display: flex;
            flex-direction: column;
            gap: 16px;
        `,
    },

    productImageAndInfoWrapper: {
        css: css`
            display: grid;
            grid-template-columns: 72px 1fr;
            gap: 12px;
            ${xScreen("lt", "sm")} {
                grid-template-columns: 72px 1fr;
            }
        `,
    },

    productImage: {
        width: "72px",
        height: "72px",
        css: css`
            aspect-ratio: 1;
        `,
    },

    productInfoWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },

    productDescriptionStyles: {
        productDetailDescription: {
            ...sharedProductDescriptionStyles().productDetailDescription,
            css: css`
                ${sharedProductDescriptionStyles().productDetailDescription?.css}
                margin-bottom:8px;
                &&& ${TypographyStyle} {
                    font-weight: ${fontConfig.weight.medium};
                }
            `,
        },
        productDetailLink: {
            ...sharedProductDescriptionStyles().productDetailLink,
            css: css`
                ${sharedProductDescriptionStyles().productDetailLink?.css}
                margin-bottom:8px;
                &&& ${TypographyStyle} {
                    font-weight: ${fontConfig.weight.medium};
                }
            `,
        },
    },

    productBrandStyles: {
        wrapper: {
            css: css`
                margin-top: 2px;
            `,
        },
    },

    checkboxDataTableCell: {
        css: css`
            text-align: center;
        `,
    },

    requestCertificationsModalButtonsWrapper: {
        ...modalButtonsActionStyles.buttonsGridItem,
        css: css`
            ${modalButtonsActionStyles.buttonsGridItem?.css}
            padding-top: 16px;
            border-top: 1px solid ${color("common.border")};
        `,
    },
    requestCertificationsModalUpdateRequestsButton: { ...modalButtonsActionStyles.submitButton },
    requestCertificationsModalCancelButton: { ...modalButtonsActionStyles.cancelButton },

    dataTableHeader: {
        css: css`
            &&& {
                border-left: 0;
                border-right: 0;
            }
        `,
    },
    dataTableRow: {
        css: css`
            border-bottom: 1px solid ${color("common.ui3")};
            &:nth-child(2n) {
                background: ${color("common.ui1")};
            }
        `,
    },
};

const styles = requestCerificationModalStyles;
const RequestCerificationsModal: FC<Props> = ({
    isOpen,
    closeModalHandler,
    cart,
    isMobileScreen,
    updateCartLinesCertifications,
}) => {
    const toasterContext = React.useContext(ToasterContext);
    const [updateRequestCertificationsInProgress, setUpdateRequestCertificationsInProgress] = useState(false);
    const [productCertSelections, setProductCertSelections] = useState<Record<string, { eis: boolean; mfr: boolean }>>(
        {},
    );

    const allProductsEis =
        Object.values(productCertSelections).length > 0 && Object.values(productCertSelections).every(sel => sel.eis);
    const allProductsMfr =
        Object.values(productCertSelections).length > 0 && Object.values(productCertSelections).every(sel => sel.mfr);

    React.useEffect(() => {
        if (cart && cart.cartLines?.length) {
            const initialSelections: Record<string, { eis: boolean; mfr: boolean }> = {};
            cart.cartLines.forEach(line => {
                initialSelections[line.id] = {
                    eis: line.properties["eis/GCGCertification"] === "true",
                    mfr: line.properties["mfrCertification"] === "true",
                };
            });
            setProductCertSelections(initialSelections);
        }
    }, [cart]);

    const onAllProductsEisHanlder = (event: React.SyntheticEvent, value: boolean) => {
        setProductCertSelections(prev => {
            const updated = { ...prev };
            Object.keys(updated).forEach(id => {
                updated[id].eis = value;
            });
            return updated;
        });
    };

    const onAllProductsMfrHanlder = (event: React.SyntheticEvent, value: boolean) => {
        setProductCertSelections(prev => {
            const updated = { ...prev };
            Object.keys(updated).forEach(id => {
                updated[id].mfr = value;
            });
            return updated;
        });
    };

    const onProductCertChange = (lineId: string, type: "eis" | "mfr", value: boolean) => {
        setProductCertSelections(prev => {
            const updated = { ...prev, [lineId]: { ...prev[lineId], [type]: value } };
            return updated;
        });
    };

    if (!cart) {
        return null;
    }

    const refElement = React.createRef<HTMLDivElement>();

    const updateRequestsHandler = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        const cartLines = Object.keys(productCertSelections).map(id => {
            const cert = productCertSelections[id];

            return {
                cartLineId: id,
                mfrCertification: cert.mfr,
                eisgcgCertification: cert.eis,
            };
        });
        setUpdateRequestCertificationsInProgress(true);
        updateCartLinesCertifications({
            cartLineCertifications: cartLines,
            cartId: cart.id,
            onSuccess: () => {
                toasterContext.addToast({
                    body: translate("Requested certifications have been updated successfully"),
                    messageType: "success",
                });
            },
            onError: (errorMessage: string) => {
                toasterContext.addToast({
                    body: errorMessage,
                    messageType: "danger",
                });
            },
            onComplete: () => {
                setUpdateRequestCertificationsInProgress(false);
                closeModalHandler && closeModalHandler();
            },
        } as any);
    };

    return (
        <Modal
            {...styles.requestCertificationsModal}
            headline={siteMessage("Custom_Request_Certifications_Modal_Header")}
            isOpen={isOpen}
            handleClose={closeModalHandler}
        >
            <StyledWrapper {...styles.requestCertificationsModalWrapper}>
                <Typography {...styles.requestCertificationsModalSubcopy}>
                    {siteMessage("Custom_Request_Certifications_Modal_Subcopy")}
                </Typography>

                <StyledWrapper {...styles.requestCertificationsModalSelectAllChecboxesWrapper} as="fieldset">
                    <Checkbox
                        checked={allProductsEis}
                        data-test-selector="all_products_eis_certificate"
                        onChange={onAllProductsEisHanlder}
                    >
                        {siteMessage("Custom_Request_EIS_Certs_All")}
                    </Checkbox>
                    <Checkbox
                        checked={allProductsMfr}
                        data-test-selector="all_products_mfr_certificate"
                        onChange={onAllProductsMfrHanlder}
                    >
                        {siteMessage("Custom_Request_Mfr_Certs_All")}
                    </Checkbox>
                </StyledWrapper>

                <FancyScroll refElement={refElement}>
                    <StyledWrapper {...styles.requestCertificationsModalProductsSelectionTableWrapper} ref={refElement}>
                        <DataTable>
                            <DataTableHead>
                                {!isMobileScreen && (
                                    <>
                                        <DataTableHeader {...styles.dataTableHeader}>
                                            {translate("EIS Cert.")}
                                        </DataTableHeader>
                                        <DataTableHeader {...styles.dataTableHeader}>
                                            {translate("MFR Cert.")}
                                        </DataTableHeader>
                                    </>
                                )}
                                <DataTableHeader {...styles.dataTableHeader}>{translate("Product")}</DataTableHeader>
                            </DataTableHead>
                            <DataTableBody>
                                {cart.cartLines?.map(line => (
                                    <DataTableRow key={line.id} {...styles.dataTableRow}>
                                        {!isMobileScreen && (
                                            <>
                                                <DataTableCell {...styles.checkboxDataTableCell}>
                                                    <Checkbox
                                                        checked={productCertSelections[line.id]?.eis || false}
                                                        onChange={(e, value) =>
                                                            onProductCertChange(line.id, "eis", value)
                                                        }
                                                    />
                                                </DataTableCell>
                                                <DataTableCell {...styles.checkboxDataTableCell}>
                                                    <Checkbox
                                                        checked={productCertSelections[line.id]?.mfr || false}
                                                        onChange={(e, value) =>
                                                            onProductCertChange(line.id, "mfr", value)
                                                        }
                                                    />
                                                </DataTableCell>
                                            </>
                                        )}
                                        <DataTableCell
                                            {...styles.productDataTableCell}
                                            data-test-selector="productsTable_tableCell_products"
                                        >
                                            {isMobileScreen && (
                                                <>
                                                    <Checkbox
                                                        checked={productCertSelections[line.id]?.eis || false}
                                                        onChange={(e, value) =>
                                                            onProductCertChange(line.id, "eis", value)
                                                        }
                                                    >
                                                        {translate("EIS Cert.")}
                                                    </Checkbox>
                                                    <Checkbox
                                                        checked={productCertSelections[line.id]?.mfr || false}
                                                        onChange={(e, value) =>
                                                            onProductCertChange(line.id, "mfr", value)
                                                        }
                                                    >
                                                        {translate("MFR Cert.")}
                                                    </Checkbox>
                                                </>
                                            )}
                                            <StyledWrapper {...styles.productImageAndInfoWrapper}>
                                                <LazyImage src={line.smallImagePath} {...styles.productImage} />
                                                <StyledWrapper {...styles.productInfoWrapper}>
                                                    <ProductDescription
                                                        product={line}
                                                        extendedStyles={styles.productDescriptionStyles}
                                                    />
                                                    <ProductPartNumbers
                                                        productNumber={line.erpNumber}
                                                        showProductNumber={true}
                                                        manufacturerItem={line.manufacturerItem}
                                                        showLabel={true}
                                                    />
                                                    {line.brand && (
                                                        <ProductBrand
                                                            brand={line.brand}
                                                            showLabel={true}
                                                            extendedStyles={styles.productBrandStyles}
                                                        />
                                                    )}
                                                </StyledWrapper>
                                            </StyledWrapper>
                                        </DataTableCell>
                                    </DataTableRow>
                                ))}
                            </DataTableBody>
                        </DataTable>
                    </StyledWrapper>
                </FancyScroll>

                <StyledWrapper {...styles.requestCertificationsModalButtonsWrapper}>
                    <Button
                        {...styles.requestCertificationsModalCancelButton}
                        onClick={() => {
                            closeModalHandler && closeModalHandler();
                        }}
                    >
                        {translate("Cancel")}
                    </Button>
                    <Button
                        {...styles.requestCertificationsModalUpdateRequestsButton}
                        onClick={e => updateRequestsHandler(e)}
                        data-test-selector="productAddToListModal_addButton"
                        disabled={updateRequestCertificationsInProgress}
                    >
                        {translate("Update Requests")}
                    </Button>
                </StyledWrapper>
            </StyledWrapper>
        </Modal>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(RequestCerificationsModal);
